import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { 
  Users, 
  Briefcase, 
  CheckCircle, 
  Clock, 
  Activity, 
  Layers, 
  Calendar, 
  AlertCircle, 
  Plus, 
  ArrowUpRight, 
  Sparkles, 
  UserCheck, 
  TrendingUp, 
  CheckCircle2, 
  FileText,
  Search
} from 'lucide-react';
import { Link } from 'react-router-dom';
import CreateDailyAssignmentModal from './CreateDailyAssignmentModal';
import UserPerformanceModal from './UserPerformanceModal';

const ATTENDANCE_BADGES = {
  Present: 'bg-emerald-500 text-white',
  Incomplete: 'bg-rose-500 text-white',
  Absent: 'bg-rose-500 text-white',
  'No Assignment': 'bg-slate-400 text-white',
  Late: 'bg-amber-500 text-white',
  Leave: 'bg-blue-500 text-white',
  Holiday: 'bg-purple-500 text-white'
};

const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [teamProgressData, setTeamProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUserForPerf, setSelectedUserForPerf] = useState(null);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
      const headers = { Authorization: `Bearer ${token}` };

      const [analyticsRes, progressRes] = await Promise.all([
        axios.get(window.API_URL + '/api/portal/admin/analytics', { headers }),
        axios.get(window.API_URL + '/api/portal/admin/daily-assignments/summary/today', { headers })
      ]);

      setStats(analyticsRes.data);
      setTeamProgressData(progressRes.data);
    } catch (err) {
      console.error("Failed to fetch admin overview data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const summary = teamProgressData?.summary || {};
  const teamList = teamProgressData?.teamProgress || [];

  // Filter team members
  const filteredTeam = teamList.filter(item => {
    if (roleFilter !== 'All' && item.role !== roleFilter) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchName = item.fullName?.toLowerCase().includes(term);
      const matchEmail = item.email?.toLowerCase().includes(term);
      const matchDept = item.department?.toLowerCase().includes(term);
      if (!matchName && !matchEmail && !matchDept) return false;
    }
    return true;
  });

  return (
    <div className="space-y-8 pb-12">
      
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white p-6 sm:p-8 rounded-3xl shadow-xl border border-slate-800">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#f06600] animate-pulse"></span>
            <span className="text-xs font-bold uppercase tracking-widest text-[#f06600]">
              Operational Command Center
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
            Admin Dashboard
          </h2>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            Real-time daily task assignment, team productivity, and attendance tracking.
          </p>
        </div>

        {/* Primary Action Button */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-[#f06600] hover:bg-orange-600 text-white font-bold px-5 py-3 rounded-2xl shadow-lg shadow-orange-500/30 hover:shadow-orange-500/45 transition-all flex items-center gap-2 text-xs sm:text-sm cursor-pointer transform hover:-translate-y-0.5"
          >
            <Plus className="w-4 h-4" />
            <span>Create Daily Assignment</span>
          </button>
        </div>
      </div>

      {/* Top 8 Core Operational Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 sm:gap-4">
        
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm text-center">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Total Interns</span>
          <div className="text-2xl sm:text-3xl font-black text-blue-600 tracking-tight">{stats?.totalInterns || 0}</div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm text-center">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Team Members</span>
          <div className="text-2xl sm:text-3xl font-black text-[#f06600] tracking-tight">{stats?.totalEmployees || 0}</div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm text-center">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Active Users</span>
          <div className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">{stats?.activeUsers || 0}</div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm text-center">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Today's Tasks</span>
          <div className="text-2xl sm:text-3xl font-black text-purple-600 tracking-tight">{summary.totalTodayTasks || 0}</div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm text-center">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Completed Today</span>
          <div className="text-2xl sm:text-3xl font-black text-emerald-600 tracking-tight">{summary.completedTodayTasks || 0}</div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm text-center">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Pending Today</span>
          <div className="text-2xl sm:text-3xl font-black text-amber-600 tracking-tight">{summary.pendingTodayTasks || 0}</div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm text-center">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Submitted / Review</span>
          <div className="text-2xl sm:text-3xl font-black text-indigo-600 tracking-tight">{summary.submittedTodayTasks || 0}</div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm text-center">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Present Today</span>
          <div className="text-2xl sm:text-3xl font-black text-emerald-600 tracking-tight">{summary.presentTodayCount || 0}</div>
        </div>

      </div>

      {/* Quick Action Navigation Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 rounded-2xl p-4 text-left transition-all group flex flex-col justify-between"
        >
          <div className="flex items-center justify-between mb-2">
            <Layers className="w-5 h-5 text-[#f06600]" />
            <Plus className="w-4 h-4 text-[#f06600] opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <div>
            <div className="font-extrabold text-sm text-slate-900 group-hover:text-[#f06600] transition-colors">+ Assign Daily Tasks</div>
            <div className="text-xs text-slate-500 mt-0.5">Multi-task workflow</div>
          </div>
        </button>

        <Link
          to="/dashboard/admin/tasks?filter=Submitted"
          className="bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded-2xl p-4 text-left transition-all group flex flex-col justify-between"
        >
          <div className="flex items-center justify-between mb-2">
            <CheckCircle2 className="w-5 h-5 text-purple-600" />
            <ArrowUpRight className="w-4 h-4 text-purple-600" />
          </div>
          <div>
            <div className="font-extrabold text-sm text-slate-900 group-hover:text-purple-600 transition-colors">Review Submissions</div>
            <div className="text-xs text-slate-500 mt-0.5">{summary.submittedTodayTasks || 0} awaiting review</div>
          </div>
        </Link>

        <Link
          to="/dashboard/admin/attendance"
          className="bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-2xl p-4 text-left transition-all group flex flex-col justify-between"
        >
          <div className="flex items-center justify-between mb-2">
            <UserCheck className="w-5 h-5 text-emerald-600" />
            <ArrowUpRight className="w-4 h-4 text-emerald-600" />
          </div>
          <div>
            <div className="font-extrabold text-sm text-slate-900 group-hover:text-emerald-600 transition-colors">Manage Attendance</div>
            <div className="text-xs text-slate-500 mt-0.5">Auto &amp; overrides</div>
          </div>
        </Link>

        <Link
          to="/dashboard/admin/users"
          className="bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 rounded-2xl p-4 text-left transition-all group flex flex-col justify-between"
        >
          <div className="flex items-center justify-between mb-2">
            <Users className="w-5 h-5 text-blue-600" />
            <ArrowUpRight className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <div className="font-extrabold text-sm text-slate-900 group-hover:text-blue-600 transition-colors">+ Manage Users</div>
            <div className="text-xs text-slate-500 mt-0.5">Interns &amp; team</div>
          </div>
        </Link>

        <Link
          to="/dashboard/admin/cms"
          className="bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-2xl p-4 text-left transition-all group flex flex-col justify-between"
        >
          <div className="flex items-center justify-between mb-2">
            <Sparkles className="w-5 h-5 text-amber-600" />
            <ArrowUpRight className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <div className="font-extrabold text-sm text-slate-900 group-hover:text-amber-600 transition-colors">Website CMS</div>
            <div className="text-xs text-slate-500 mt-0.5">Dynamic web pages</div>
          </div>
        </Link>

        <Link
          to="/dashboard/admin/internal-projects"
          className="bg-teal-500/10 hover:bg-teal-500/20 border border-teal-500/30 rounded-2xl p-4 text-left transition-all group flex flex-col justify-between"
        >
          <div className="flex items-center justify-between mb-2">
            <Briefcase className="w-5 h-5 text-teal-600" />
            <ArrowUpRight className="w-4 h-4 text-teal-600" />
          </div>
          <div>
            <div className="font-extrabold text-sm text-slate-900 group-hover:text-teal-600 transition-colors">Internal Projects</div>
            <div className="text-xs text-slate-500 mt-0.5">Client &amp; deliverables</div>
          </div>
        </Link>

      </div>

      {/* TODAY'S TEAM PROGRESS TABLE (The Core Required Section) */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        
        {/* Table Header & Filter Bar */}
        <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#f06600]"></span>
              <h3 className="text-lg font-black text-slate-900 tracking-tight">
                Today's Team Progress &amp; Attendance
              </h3>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Click any member to open their complete day-by-day task breakdown and performance history.
            </p>
          </div>

          {/* Search & Filters */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search member or department..."
                className="pl-9 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#f06600]/30 focus:border-[#f06600] w-52 sm:w-64"
              />
            </div>

            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#f06600]/30"
            >
              <option value="All">All Roles</option>
              <option value="Developer">Developers</option>
              <option value="Employee">Employees</option>
              <option value="HR">HR</option>
              <option value="Intern">Interns</option>
              <option value="Designer">Designers</option>
              <option value="Team Lead">Team Leads</option>
              <option value="Manager">Managers</option>
            </select>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-[11px] font-bold uppercase tracking-wider">
                <th className="py-3.5 px-6">Member</th>
                <th className="py-3.5 px-4 text-center">Assigned</th>
                <th className="py-3.5 px-4 text-center text-emerald-600">Completed</th>
                <th className="py-3.5 px-4 text-center text-amber-600">Pending</th>
                <th className="py-3.5 px-6 text-center">Progress</th>
                <th className="py-3.5 px-6 text-center">Attendance</th>
                <th className="py-3.5 px-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {filteredTeam.map(member => (
                <tr
                  key={member._id}
                  onClick={() => setSelectedUserForPerf(member._id)}
                  className="hover:bg-orange-50/30 transition-colors cursor-pointer group"
                >
                  {/* Member info */}
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-800 font-bold text-xs flex items-center justify-center group-hover:bg-[#f06600] group-hover:text-white transition-colors">
                        {member.fullName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <span className="font-extrabold text-slate-900 block group-hover:text-[#f06600] transition-colors">
                          {member.fullName}
                        </span>
                        <span className="text-xs text-slate-500">
                          {member.role} • {member.department}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Assigned */}
                  <td className="py-4 px-4 text-center font-bold text-slate-800">
                    {member.assigned}
                  </td>

                  {/* Completed */}
                  <td className="py-4 px-4 text-center font-bold text-emerald-600">
                    {member.completed}
                  </td>

                  {/* Pending */}
                  <td className="py-4 px-4 text-center font-bold text-amber-600">
                    {member.pending}
                  </td>

                  {/* Progress Bar */}
                  <td className="py-4 px-6 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <span className={`text-xs font-black w-9 text-right ${member.completionRate === 100 ? 'text-emerald-600' : member.completionRate >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>
                        {member.completionRate}%
                      </span>
                      <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden border border-slate-200">
                        <div
                          className={`h-full transition-all duration-500 ${member.completionRate === 100 ? 'bg-emerald-500' : member.completionRate >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`}
                          style={{ width: `${member.completionRate}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>

                  {/* Attendance Status */}
                  <td className="py-4 px-6 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-sm inline-block ${ATTENDANCE_BADGES[member.attendanceStatus] || 'bg-slate-400 text-white'}`}>
                      {member.attendanceStatus}
                    </span>
                    {member.isOverridden && (
                      <span className="block text-[10px] text-amber-600 font-bold mt-0.5">
                        ★ Overridden
                      </span>
                    )}
                  </td>

                  {/* Action Link */}
                  <td className="py-4 px-6 text-right">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedUserForPerf(member._id);
                      }}
                      className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-[#f06600] text-slate-700 hover:text-white text-xs font-bold transition-colors inline-flex items-center gap-1 shadow-sm"
                    >
                      <span>View History</span>
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}

              {filteredTeam.length === 0 && (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-slate-500 text-xs font-medium">
                    No active team members found matching your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </div>

      {/* Create Daily Assignment Modal */}
      <CreateDailyAssignmentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchDashboardData}
      />

      {/* User Historical Performance Modal */}
      {selectedUserForPerf && (
        <UserPerformanceModal
          isOpen={Boolean(selectedUserForPerf)}
          userId={selectedUserForPerf}
          onClose={() => setSelectedUserForPerf(null)}
          onRefresh={fetchDashboardData}
        />
      )}

    </div>
  );
};

export default AdminOverview;
